<template>
  <div class="jps-interact-panel" v-if="data">
    <template v-if="!isWebParts">
      <UiContentTooltip ref="contentTooltip" :other-toggle="showToolsButton" class="ui-control-tooltip-hide">
        <UiInteractPanelInner :type="type" :data="data" />
      </UiContentTooltip>
      <slot />
      <div ref="showToolsButton">
        <UiButtonIcon
          tag="button"
          class="jps-icon-button jps-share-icon jps-blue"
          tab-index="0"
          :arialabel="lss('SNSでシェア', 'Share')"
          icon="mdi/share-variant"
          @click="showTools()"
        />
      </div>
      <div v-if="!isHina">
        <UiButtonIcon
          tag="button"
          class="jps-icon-button jps-fav-icon jps-blue"
          :class="{
            'is-favorite': _isFavorite,
          }"
          :arialabel="lss('ノートに追加', 'Add to note')"
          :icon="_isFavorite ? 'mdi/heart' : 'mdi/heart-outline'"
          @click.stop="addToFavorite()"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { UiContentTooltip } from '#components'

const { current } = usePageStore()
const { lss, langPath } = useI18n()
const route = useRoute()
const router = useRouter()
const { snackbar } = useSnackbar()
const { isWebParts } = useWebParts()
const { addActionEvent } = useLogStore()
const { addFavorite, removeFavorite, isFavorite } = useNoteStore()
const { isHina } = useServiceType()

interface Props {
  type: Key
  image?: ImageSelection
  data?: { id: string }
}
const props = withDefaults(defineProps<Props>(), {
})

const showToolsButton: Ref<HTMLElement> = ref()
const contentTooltip = ref<InstanceType<typeof UiContentTooltip>>()
const showTools = () => {
  contentTooltip.value.toggle()
}

const addToFavorite = () => {
  if (_isFavorite.value)
    removeFavorite(props.type, props.data.id)
  else {
    addFavorite(props.type, props.data.id, props.image)
    addActionEvent(
      'fav',
      route.fullPath,
      props.type + '-' + props.data.id,
      'UiInteractPanel',
    )
    snackbar(
      {
        message: {
          ja: 'ノートに登録しました',
          en: 'Added to note',
        },
        actionText: {
          ja: 'ノートを見る',
          en: 'See your note',
        },
        action: () => {
          router.push(langPath('/mynote'))
        },
      }
    )
  }
}

const _isFavorite = computed(() => {
  return isFavorite(props.type, props.data.id)
})
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.jps-interact-inner {
  display: flex;
  align-items: center;
  .jps-icon {
    font-size: 2rem;
    color: $white !important;
    padding: 0.5rem;
    height: 3rem;
  }
  a {
    padding: 0.5rem;
    height: 3rem;
    color: $white !important  ;

    img {
      height: 2rem;
      width: 2rem;
      max-width: 2rem;
      max-height: 2rem;
    }
  }
  button {
    width: 3rem;
  }
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.jps-interact-panel {
  user-select: none;
  padding: 0.5rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  > * {
    display: block;
    width: 32px;
    height: 32px;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    img {
      flex-shrink: 0;
    }
  }

  .jps-fav-icon {
    transition: color 0.15s;
    &:hover {
      color: $jps-key-red !important;
    }
    &.is-favorite {
      color: $jps-key-red;
    }
  }
  @include is-print() {
    display: none;
  }
}
.jps-fav-icon, .jps-share-icon {
  &:focus {
    // 青円のフォーカスラウンドが表示されるのを取り消す
    // クリック時は何も表示しない
    outline: none !important;
    border-radius: 4px  !important;
  }
  &:focus-visible {
    // タブ移動時は表示する
    outline: 2px solid $jps-key-blue-hover !important;
    border-radius: 4px !important;
  }
}
.ui-control-tooltip-hide {
  width: 0px;
  height: 0px;
  padding: 0;
}
</style>
